import { useEffect } from 'react';

/**
 * Prevents React Aria scrollbar compensation that can cause layout shifts
 *
 * React Aria automatically adds padding-right to the html element when opening
 * modal/popover components to compensate for the hidden scrollbar. It can create
 * an unwanted visual layout shift.
 *
 * This hook observes changes to the html element's style attribute and immediately
 * resets any padding-right while preserving overflow:hidden for scroll prevention.
 *
 * @example
 * ```tsx
 * function MyPopoverComponent() {
 *   usePreventScrollbarCompensation();
 *   return <Popover>...</Popover>;
 * }
 * ```
 */
export const usePreventScrollbarCompensation = (): void => {
  useEffect(() => {
    const resetScrollbarCompensation = (): void => {
      const htmlElement = document.documentElement;
      if (htmlElement.style.overflow === 'hidden' && htmlElement.style.paddingRight) {
        htmlElement.style.paddingRight = '0';
      }
    };

    const observer = new MutationObserver(resetScrollbarCompensation);

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style'],
    });

    return () => {
      observer.disconnect();
    };
  }, []);
};
